import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { CerberusApi } from '@hcs/cerberus';
import {
  DateStr,
  PlaceIdInput,
  PlaceLookupDocument,
  PlaceLookupQuery,
  PlaceLookupQueryVariables,
  PlaceLookupWithListingStatsDocument,
  PlaceLookupWithListingStatsQuery,
  PlaceLookupWithListingStatsQueryVariables,
} from '@hcs/types';
import { hookEnabledError } from '@hcs/utils';

export const QUERY_KEY_PLACE_LOOKUP = 'QUERY_KEY_PLACE_LOOKUP';
export const QUERY_KEY_PLACE_LOOKUP_WITH_LISTING_STATS =
  'QUERY_KEY_PLACE_LOOKUP_WITH_LISTING_STATS';

export type PlaceLookupUseQueryOptions = UseQueryOptions<
  PlaceLookupQuery['lookupPlace'],
  AxiosError,
  PlaceLookupQuery['lookupPlace'],
  [typeof QUERY_KEY_PLACE_LOOKUP, PlaceIdInput | undefined]
>;
export const usePlaceLookup = (
  placeIdInput: PlaceIdInput | undefined,
  options?: PlaceLookupUseQueryOptions,
): UseQueryResult<PlaceLookupQuery['lookupPlace']> => {
  return useQuery({
    queryKey: [QUERY_KEY_PLACE_LOOKUP, placeIdInput],

    queryFn: async () => {
      if (placeIdInput) {
        const response = await CerberusApi.fetchQuery<
          PlaceLookupQuery,
          PlaceLookupQueryVariables
        >(PlaceLookupDocument, {
          placeIdInput,
        });
        return response.lookupPlace;
      } else {
        throw hookEnabledError('usePlaceLookup', 'placeIdInput');
      }
    },

    ...options,

    enabled:
      !!placeIdInput &&
      (options?.enabled !== undefined ? options.enabled : true),
  });
};

export const usePlaceLookupWithListingStats = ({
  placeId,
  fromDate,
  toDate,
}: {
  placeId: string | null | undefined;
  fromDate?: DateStr;
  toDate?: DateStr;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [
      QUERY_KEY_PLACE_LOOKUP_WITH_LISTING_STATS,
      placeId,
      fromDate,
      toDate,
    ],

    queryFn: async () => {
      if (!placeId) {
        throw new Error('placeId is required');
      }
      const placeIdInput = { placeId };
      const response = await CerberusApi.fetchQuery<
        PlaceLookupWithListingStatsQuery,
        PlaceLookupWithListingStatsQueryVariables
      >(PlaceLookupWithListingStatsDocument, {
        placeIdInput: {
          placeId,
        },
        fromDate:
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          fromDate as PlaceLookupWithListingStatsQueryVariables['fromDate'],
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        toDate: toDate as PlaceLookupWithListingStatsQueryVariables['toDate'],
      });
      // Seed cache for usePlaceLookup since this query returns all of the same
      // place fields (FRAGMENT_PLACE_LOOKUP_FIELDS) plus listing stats
      queryClient.setQueryData(
        [QUERY_KEY_PLACE_LOOKUP, placeIdInput],
        response.lookupPlace,
      );
      return response;
    },
    enabled: !!placeId,
  });
};
